/*
 * BACKEND MIGRATION TO REMOVE THIS FILE
 *
 * Temporary responsibility:
 * - Normalize Planner-facing patient and event view models while the frontend
 *   still receives mixed EHR appointment, Recall scheduled-bot, Encounter,
 *   and Conversation-shaped records.
 * - Keep UI components from inspecting source-specific fields such as
 *   `appointment`, `scheduledBot`, `encounter`, or `session`.
 * - Provide compatibility adapters for legacy components that still consume
 *   the unified patient item shape ({ matchKind, localPatient, ehrPatient }).
 *
 * Backend replacement:
 * - Add Planner endpoints that return card-ready `PlannerPatient` and
 *   `PlannerEvent` objects directly:
 *   GET /api/v1/planner/workspace?date=YYYY-MM-DD&timeZone=<IANA>
 *   {
 *     sections: [{ key, label, defaultOpen, events: PlannerEvent[] }]
 *   }
 * - Events should include action metadata/ids, not embedded raw source docs.
 *
 * Delete this file when:
 * - PlannerPage, CreateSessionForm, and the patient panel
 *   receive backend PlannerPatient / PlannerEvent models directly.
 * - No frontend component needs to derive a planner model from raw EHR,
 *   Recall, Encounter, or Conversation records.
 */

import {formatDobShort, isNonEnglish} from '../../constants/designTokens';
import {
  isEncounterRecording,
  isPhoneEncounter,
  isTelehealthEncounter,
  resolveEncounterModality,
  toJsDate,
} from '../../services/encounterUtils';
import {
  PLANNER_SCHEDULE_SECTIONS,
  getPlannerScheduleTotal,
  getVisiblePlannerScheduleSections,
} from './plannerScaffoldData';
import {
  conversationRecordedMinutes,
  encounterDetailLine,
  encounterDisplayDate,
  encounterLanguage,
  encounterRecordedMinutes,
  isPlaceholderEncounterSummary,
  recordedMinutesFromSeconds,
  recordedMinutesFromValue,
  representativeEncounterSession,
} from './plannerEncounterMetadata';
import {
  normalizePatientNameParts,
  patientNameHasRepeatedMiddleToken,
} from '../../utils/patientNames';
import {normalizePatientMrn} from '../../utils/patientMrn';

// Shared trim-to-string helper for every planner module — null/undefined
// become '', everything else is stringified and trimmed.
export const clean = (value: any) => String(value ?? '').trim();

const firstValue = (...values: any[]) => {
  const value = values.find(
    candidate => candidate != null && clean(candidate) !== '',
  );
  return value ?? '';
};

// Parse a planner card time label like "9:30 AM" into { hour, minute }
// (24-hour clock), or null when the label isn't a plain wall-clock time.
export function parsePlannerTimeLabel(label: any) {
  const match = clean(label).match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (!match) return null;
  const [, hourText, minuteText = '0', periodText] = match;
  let hour = Number(hourText);
  const minute = Number(minuteText);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  if (periodText.toUpperCase() === 'AM' && hour === 12) hour = 0;
  if (periodText.toUpperCase() === 'PM' && hour !== 12) hour += 12;
  return {hour, minute};
}

function splitPatientName(fullName: any) {
  const name = clean(fullName);
  if (!name) return {firstName: '', lastName: ''};
  const parts = name.split(/\s+/);
  if (parts.length === 1) return {firstName: parts[0], lastName: ''};
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
}

function languageLabel(...values: any[]) {
  return values.map(clean).find(Boolean) || '';
}

function titleCase(value: any) {
  return clean(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, char => char.toUpperCase());
}

function plannerPatientId({localId, ehrId, fallbackId}: Record<string, any>) {
  if (localId) return `local_${localId}`;
  if (ehrId) return `ehr_${ehrId}`;
  return clean(fallbackId) || null;
}

function ehrSourceIdForPatientItem(item: any) {
  // Identity-key source for dedup only. Deliberately does NOT fall back to a
  // local patient's fhir_patient_id link: under strict separation a linked
  // local and its EHR record are distinct entities, so they must dedupe (and
  // surface) as two separate rows rather than collapse into one.
  const direct =
    item?.ehrPatient?.ehrSourceId ||
    item?.ehrPatient?.ehr_source_id ||
    item?.ehrPatient?.id;
  if (direct) return clean(direct).split('/').pop();

  return '';
}

export function plannerPatientIdentityKeys(
  patient: any,
  {includePlannerId = true, fallbackKey = ''}: Record<string, any> = {},
) {
  if (!patient) return fallbackKey ? [fallbackKey] : [];
  const keys: string[] = [];
  const pushKey = (key: string) => {
    if (key && !keys.includes(key)) keys.push(key);
  };
  const id = clean(patient.id);
  const localId =
    clean(patient.localId) ||
    (id.startsWith('local_') ? id.slice('local_'.length) : '');
  const ehrId =
    clean(patient.ehrId) ||
    clean(patient.linkedEhrId) ||
    (id.startsWith('ehr_') ? id.slice('ehr_'.length) : '');

  if (localId) pushKey(`local:${localId}`);
  if (ehrId) pushKey(`ehr:${ehrId}`);
  if (includePlannerId && id) pushKey(`id:${id}`);
  if (!keys.length && fallbackKey) pushKey(fallbackKey);
  return keys;
}

export function plannerPatientIdentityKey(
  patient: any,
  options: Record<string, any> = {},
) {
  return plannerPatientIdentityKeys(patient, options).join('|');
}

export function plannerPatientItemIdentityKeys(item: any) {
  const keys = plannerPatientIdentityKeys(
    {
      localId: clean(item?.localPatient?.id),
      ehrId: ehrSourceIdForPatientItem(item),
    },
    {
      includePlannerId: false,
    },
  );
  if (!keys.length && item?.id) return [`item:${item.id}`];
  return keys;
}

export function plannerPatientsShareIdentity(left: any, right: any) {
  const leftKeys = new Set(plannerPatientIdentityKeys(left));
  if (!leftKeys.size) return false;
  return plannerPatientIdentityKeys(right).some(key => leftKeys.has(key));
}

function matchKindForIds(localId: any, ehrId: any, linkedEhrId: any = '') {
  if (localId && (ehrId || linkedEhrId)) return 'linked';
  if (localId) return 'localOnly';
  if (ehrId) return 'ehrOnly';
  return 'unknown';
}

export type PlannerPatientMatchKind =
  | 'linked'
  | 'localOnly'
  | 'ehrOnly'
  | 'unknown';

// Kept as a type alias (not an interface) so it stays assignable to the
// `Record<string, unknown>` parameter of normalizePatientNameParts; interfaces
// are not implicitly assignable to index-signature types.
export type PlannerPatient = {
  id: string | null;
  matchKind: PlannerPatientMatchKind;
  localId: string | null;
  ehrId: string | null;
  linkedEhrId: string | null;
  firstName: string;
  middleName: string;
  lastName: string;
  displayName: string;
  fullName: string;
  language: string;
  dialect: string;
  mrn: string;
  dob: any;
  gender: string;
  phone: string;
  email: string;
  accountNo: string;
  pcp: string;
};

export interface PlannerEventActions {
  canResume: boolean;
  canEnd: boolean;
  canRestore: boolean;
  canCancel: boolean;
  canReschedule: boolean;
  resumeSession: any;
  endEncounter: any;
  restoreSession: any;
  scheduledBot: any;
  phoneCall: any;
  telephoneEncounter: any;
}

export interface PlannerEvent {
  id: string;
  stage: string;
  source: string;
  sourceId: string;
  patient: PlannerPatient | null;
  patientName: string;
  time: string;
  sortDate: any;
  language: string;
  isTelehealth: boolean;
  durationLabel: string;
  recordedMinutes: number;
  detailLine: string;
  summaryText: string;
  appointmentSummary: string;
  summaryVariants: string[];
  summarySearchText: string;
  accentKey: string;
  // Delivery modality of the session: 'video' | 'phone' | 'in_person'.
  modality: string;
  // Solo provider dictation — rows label it "Dictation" and suppress the
  // delivery-modality pill (there is no visit being delivered).
  isDictation: boolean;
  statusLabel: string;
  metadataPills: any[];
  actions: PlannerEventActions;
}

export interface PlannerRecentActivity {
  id: string;
  stage: string;
  source: string;
  sourceId: string;
  patient: PlannerPatient | null;
  patientName: string;
  occurredAt: any;
  sortDate: any;
  language: string;
  durationLabel: string;
  recordedMinutes: number;
  detailLine: string;
  accentKey: string;
  modality: string;
  isDictation: boolean;
  summary: string;
  summaryText: string;
  metadataText: string;
  actions: PlannerEventActions;
}

function buildPlannerPatient({
  localId = '',
  ehrId = '',
  linkedEhrId = '',
  fallbackId = '',
  firstName = '',
  middleName = '',
  lastName = '',
  language = '',
  dialect = '',
  mrn = '',
  dob = null,
  gender = '',
  phone = '',
  email = '',
  accountNo = '',
  pcp = '',
}: Record<string, any> = {}): PlannerPatient {
  const cleanLocalId = clean(localId);
  const cleanEhrId = clean(ehrId);
  const cleanLinkedEhrId = clean(linkedEhrId);
  const name = normalizePatientNameParts({
    firstName,
    middleName,
    lastName,
  });

  return {
    id: plannerPatientId({
      localId: cleanLocalId,
      ehrId: cleanEhrId,
      fallbackId,
    }),
    matchKind: matchKindForIds(cleanLocalId, cleanEhrId, cleanLinkedEhrId),
    localId: cleanLocalId || null,
    ehrId: cleanEhrId || null,
    linkedEhrId: cleanLinkedEhrId || null,
    firstName: name.firstName,
    middleName: name.middleName,
    lastName: name.lastName,
    displayName: name.displayName,
    fullName: name.fullName,
    language: clean(language),
    dialect: clean(dialect),
    mrn: normalizePatientMrn(mrn),
    dob: dob || null,
    gender: clean(gender),
    phone: clean(phone),
    email: clean(email),
    accountNo: normalizePatientMrn(accountNo),
    pcp: clean(pcp),
  };
}

export function plannerPatientSourceLabel(patient: any) {
  if (!patient) return '';
  const id = clean(patient.id);
  const localId =
    clean(patient.localId) ||
    (id.startsWith('local_') ? id.slice('local_'.length) : '');
  const ehrId =
    clean(patient.ehrId) ||
    (id.startsWith('ehr_') ? id.slice('ehr_'.length) : '');
  const linkedEhrId = firstValue(
    patient.linkedEhrId,
    patient.fhir_patient_id,
    patient.ehr_patient_id,
    patient.ehrPatientId,
    patient._ehrPatientId,
    patient._linkedEhrId,
  );
  const matchKind =
    patient.matchKind || matchKindForIds(localId, ehrId, linkedEhrId);

  if (matchKind === 'linked') return 'Linked';
  if (matchKind === 'ehrOnly') return 'EHR';
  if (matchKind === 'localOnly') {
    return linkedEhrId ? 'Linked' : 'Local';
  }
  return '';
}

function patientMetadataSourceLabel(label: any) {
  const source = clean(label);
  const normalized = source.toLowerCase();
  if (normalized.includes('ehr') || normalized.includes('linked')) return '';
  return source;
}

export function plannerPatientMetadataParts(
  patient: any,
  {
    language = '',
    gateLanguageToNonEnglish = false,
    sourceLabel = undefined,
  }: Record<string, any> = {},
) {
  if (!patient) return [];
  const rawLanguage = clean(language || patient.language);
  // When gated, match the flag marker — which only renders for non-English
  // patients — so the language label and the flag agree. Row/event surfaces
  // opt in; the patient detail panel keeps the explicit language (incl. English).
  const languageLabel =
    gateLanguageToNonEnglish && !isNonEnglish(rawLanguage) ? '' : rawLanguage;
  const mrn = normalizePatientMrn(patient.mrn);
  const accountNo = normalizePatientMrn(patient.accountNo);
  const dobLabel = formatDobShort(patient.dob);
  const resolvedSourceLabel =
    sourceLabel === undefined
      ? patientMetadataSourceLabel(plannerPatientSourceLabel(patient))
      : patientMetadataSourceLabel(sourceLabel);

  return [
    languageLabel,
    resolvedSourceLabel,
    dobLabel ? `DOB ${dobLabel}` : '',
    mrn || accountNo ? `MRN ${mrn || accountNo}` : '',
  ].filter(Boolean);
}

export function plannerPatientMetadataText(
  patient: any,
  {separator = ' • ', ...options}: Record<string, any> = {},
) {
  return plannerPatientMetadataParts(patient, options).join(separator);
}

export function plannerPatientFromPatientItem(item: any) {
  if (!item) return null;
  // Unified browser items wrap their records in `localPatient` /
  // `ehrPatient`. Restore flows (Back from the note editor after an
  // interpreter or scribe session) hand us the flat local patient
  // record itself instead — treat a bare record as the local side so
  // both shapes resolve to the same planner patient.
  const localPatient = item.localPatient || (item.ehrPatient ? {} : item);
  const ehrPatient = item.ehrPatient || {};
  const plannerPatient = item.patient || {};

  return buildPlannerPatient({
    localId: localPatient.id || plannerPatient.localId,
    ehrId: ehrPatient.id || plannerPatient.ehrId,
    linkedEhrId: firstValue(
      localPatient.fhir_patient_id,
      localPatient.ehr_patient_id,
      localPatient.ehrPatientId,
      localPatient._ehrPatientId,
      localPatient._linkedEhrId,
    ),
    fallbackId: item.id,
    firstName: firstValue(
      localPatient.first_name,
      localPatient.firstName,
      ehrPatient.firstName,
      ehrPatient.first_name,
      plannerPatient.firstName,
      plannerPatient.first_name,
    ),
    middleName: firstValue(
      localPatient.middle_name,
      localPatient.middleName,
      ehrPatient.middleName,
      ehrPatient.middle_name,
      plannerPatient.middleName,
      plannerPatient.middle_name,
    ),
    lastName: firstValue(
      localPatient.last_name,
      localPatient.lastName,
      ehrPatient.lastName,
      ehrPatient.last_name,
      plannerPatient.lastName,
      plannerPatient.last_name,
    ),
    language: firstValue(
      localPatient.preferred_language,
      localPatient.preferredLanguage,
      localPatient.language,
      ehrPatient.language,
      ehrPatient.preferred_language,
      ehrPatient.preferredLanguage,
      plannerPatient.language,
      plannerPatient.preferred_language,
      plannerPatient.preferredLanguage,
    ),
    dialect: firstValue(
      localPatient.preferred_dialect,
      localPatient.preferredDialect,
      ehrPatient.dialect,
      plannerPatient.dialect,
      plannerPatient.preferred_dialect,
      plannerPatient.preferredDialect,
    ),
    mrn: firstValue(
      localPatient.mrn,
      localPatient.medicalRecordNumber,
      ehrPatient.mrn,
      ehrPatient.medicalRecordNumber,
      ehrPatient.accountNo,
      ehrPatient.account_no,
      plannerPatient.mrn,
      plannerPatient.medicalRecordNumber,
      plannerPatient.accountNo,
      plannerPatient.account_no,
    ),
    dob: firstValue(
      localPatient.date_of_birth,
      localPatient.dateOfBirth,
      localPatient.dob,
      localPatient.birthDate,
      ehrPatient.dob,
      ehrPatient.dateOfBirth,
      ehrPatient.date_of_birth,
      ehrPatient.birthDate,
      plannerPatient.dob,
      plannerPatient.dateOfBirth,
      plannerPatient.date_of_birth,
      plannerPatient.birthDate,
    ),
    gender: firstValue(
      localPatient.gender,
      ehrPatient.gender,
      plannerPatient.gender,
    ),
    phone: firstValue(
      localPatient.phone_number,
      localPatient.phoneNumber,
      localPatient.phone,
      ehrPatient.phone,
      ehrPatient.phoneNumber,
      plannerPatient.phone,
      plannerPatient.phoneNumber,
    ),
    email: firstValue(
      localPatient.email,
      ehrPatient.email,
      plannerPatient.email,
    ),
    accountNo: firstValue(
      ehrPatient.accountNo,
      ehrPatient.account_no,
      plannerPatient.accountNo,
      plannerPatient.account_no,
    ),
    pcp: firstValue(
      ehrPatient.pcp,
      ehrPatient.primaryCareProvider,
      plannerPatient.pcp,
      plannerPatient.primaryCareProvider,
    ),
  });
}

function schedulePatientIdentity(item: any) {
  const encounter = item?.encounter || {};
  const appointment = item?.appointment || {};
  const bot = item?.scheduledBot || {};

  return {
    localId: clean(
      encounter.patient_id || bot.localPatientId || bot.patient_id,
    ),
    ehrId: clean(
      encounter.ehr_patient_id ||
        appointment.patientId ||
        appointment.ehrPatientId ||
        appointment.ehr_patient_id ||
        bot.ehrPatientId ||
        bot.ehr_patient_id,
    ),
  };
}

function plannerPatientFromScheduleItem(item: any) {
  if (!item) return null;
  const {localId, ehrId} = schedulePatientIdentity(item);
  if (!localId && !ehrId) return null;

  const {firstName, lastName} = splitPatientName(item.patientName);
  const appointment = item.appointment || {};
  const bot = item.scheduledBot || {};
  const ehrPatient = item.ehrPatient || {};

  return buildPlannerPatient({
    localId,
    ehrId,
    firstName: ehrPatient.firstName || firstName,
    middleName: ehrPatient.middleName,
    lastName: ehrPatient.lastName || lastName,
    language: item.languageDialect || ehrPatient.language,
    mrn: appointment.mrn || ehrPatient.mrn || bot.mrn,
    dob:
      appointment.dob ||
      appointment.dateOfBirth ||
      ehrPatient.dob ||
      ehrPatient.dateOfBirth ||
      bot.dateOfBirth,
    gender: appointment.gender || ehrPatient.gender || bot.gender,
    phone:
      appointment.phone ||
      appointment.phoneNumber ||
      ehrPatient.phone ||
      ehrPatient.phoneNumber ||
      bot.patientPhone ||
      bot.phone,
    email: appointment.email || ehrPatient.email || bot.email,
    accountNo: appointment.accountNo || ehrPatient.accountNo,
    pcp: appointment.pcp || ehrPatient.pcp,
  });
}

// Bare EHR source id linked from a local Patient doc (FHIR `Patient/<id>`
// prefix stripped), matching the id space the EHR cache uses.
function linkedEhrPatientId(localPatient: Record<string, any> = {}) {
  const raw = firstValue(
    localPatient.fhir_patient_id,
    localPatient.ehr_patient_id,
    localPatient.ehrPatientId,
    localPatient._ehrPatientId,
    localPatient._linkedEhrId,
  );
  return clean(raw).split('/').filter(Boolean).pop() || '';
}

function plannerPatientFromEncounterView(encounter: any) {
  if (!encounter) return null;
  const localId = clean(encounter.patient_id);
  const ehrId = clean(encounter.ehr_patient_id);
  if (!localId && !ehrId) return null;

  const {firstName, lastName} = splitPatientName(encounter.patient_name);
  const patientRecord = encounter.patient || {};
  // Demographics eagerly attached by plannerEncounterHydration, mirroring how
  // the schedule attaches `item.ehrPatient` (see plannerPatientFromScheduleItem).
  // `ehrPatient` is the EHR cache record; `localPatient` is the linked local
  // Patient doc. Either may be absent; the per-row hook is the fallback.
  const ehrPatient = encounter.ehrPatient || {};
  const localPatient = encounter.localPatient || {};
  return buildPlannerPatient({
    localId,
    ehrId,
    linkedEhrId: linkedEhrPatientId(localPatient),
    firstName: firstValue(
      ehrPatient.firstName,
      localPatient.first_name,
      firstName,
    ),
    middleName: firstValue(ehrPatient.middleName, localPatient.middle_name),
    lastName: firstValue(ehrPatient.lastName, localPatient.last_name, lastName),
    language: firstValue(
      ehrPatient.language,
      localPatient.preferred_language,
      localPatient.language,
      encounter.patient_preferred_language,
      encounter.patientPreferredLanguage,
      encounter.preferred_language,
      encounter.preferredLanguage,
      patientRecord.preferred_language,
      patientRecord.preferredLanguage,
      patientRecord.language,
      // Last resort: the interpreted session language already on the encounter
      // (see encounterLanguage). EHR/local data wins; this only fills gaps so
      // interpreted recents show the flag even when every lookup misses.
      encounter.foreign_language,
      encounter.foreignLanguage,
    ),
    dialect: firstValue(
      localPatient.preferred_dialect,
      localPatient.preferredDialect,
    ),
    mrn: firstValue(
      ehrPatient.mrn,
      ehrPatient.accountNo,
      localPatient.mrn,
      localPatient.medicalRecordNumber,
      encounter.mrn,
      encounter.patient_mrn,
      encounter.patientMrn,
    ),
    dob: firstValue(
      ehrPatient.dob,
      ehrPatient.dateOfBirth,
      localPatient.date_of_birth,
      localPatient.dateOfBirth,
      localPatient.dob,
      encounter.dob,
      encounter.date_of_birth,
      encounter.dateOfBirth,
      encounter.patient_dob,
      encounter.patientDob,
    ),
    gender: firstValue(ehrPatient.gender, localPatient.gender),
    phone: firstValue(
      ehrPatient.phone,
      ehrPatient.phoneNumber,
      localPatient.phone_number,
      localPatient.phoneNumber,
      localPatient.phone,
    ),
    email: firstValue(ehrPatient.email, localPatient.email),
    accountNo: ehrPatient.accountNo,
    pcp: ehrPatient.pcp,
  });
}

function removedConversationLanguage(conversation: any) {
  return languageLabel(
    conversation?.foreign_language,
    conversation?.foreignLanguage,
    conversation?.language,
  );
}

function phoneCallLanguage(call: any) {
  return languageLabel(
    call?.foreign_language,
    call?.foreignLanguage,
    call?.language,
  );
}

function plannerPatientFromRemovedConversation(conversation: any) {
  if (!conversation) return null;
  const localId = clean(conversation.patient_id);
  const ehrId = clean(conversation.ehr_patient_id);
  if (!localId && !ehrId) return null;

  const {firstName, lastName} = splitPatientName(conversation.patient_name);
  return buildPlannerPatient({
    localId,
    ehrId,
    firstName,
    lastName,
  });
}

export function plannerPatientToPatientItem(patient: any) {
  if (!patient?.id) return null;

  const localPatient = patient.localId
    ? {
        id: patient.localId,
        first_name: patient.firstName,
        last_name: patient.lastName,
        preferred_language: patient.language || null,
        preferred_dialect: patient.dialect || null,
        mrn: patient.mrn || null,
        date_of_birth: patient.dob || null,
        gender: patient.gender || null,
        phone_number: patient.phone || null,
        email: patient.email || null,
        fhir_patient_id: patient.linkedEhrId || null,
        _linkedEhrId: patient.linkedEhrId || null,
      }
    : null;

  const ehrPatient = patient.ehrId
    ? {
        id: patient.ehrId,
        firstName: patient.firstName,
        middleName: patient.middleName,
        lastName: patient.lastName,
        language: patient.language || null,
        mrn: patient.mrn || null,
        dob: patient.dob || null,
        dateOfBirth: patient.dob || null,
        gender: patient.gender || null,
        phone: patient.phone || null,
        phoneNumber: patient.phone || null,
        email: patient.email || null,
        accountNo: patient.accountNo || null,
        pcp: patient.pcp || null,
      }
    : null;

  return {
    id: patient.id,
    matchKind: patient.matchKind,
    localPatient,
    ehrPatient,
  };
}

const STAGE_BY_SECTION_KEY = new Map(
  PLANNER_SCHEDULE_SECTIONS.map(section => [section.key, section.stage]),
);

function stageForSection(sectionKey: any) {
  return STAGE_BY_SECTION_KEY.get(sectionKey) || 'upcoming';
}

function sourceForItem(item: any) {
  if (item?.sourceType === 'ehrAppointment') return 'appointment';
  if (item?.sourceType === 'scheduledBot') return 'scheduledBot';
  if (item?.sourceType === 'conversation') return 'conversation';
  if (item?.sourceType === 'encounter') return 'encounter';
  if (item?.sourceType === 'phoneCall') return 'phoneCall';
  if (item?.appointment) return 'appointment';
  if (item?.scheduledBot) return 'scheduledBot';
  if (item?.encounter) return 'encounter';
  if (item?.phoneCall) return 'phoneCall';
  const sourceLabel = clean(item?.sourceLabel).toLowerCase();
  if (sourceLabel === 'ehr' || sourceLabel === 'ehr appointment') {
    return 'appointment';
  }
  if (sourceLabel === 'scheduled bot') return 'scheduledBot';
  return 'unknown';
}

function normalizePlannerLabel(value: any) {
  return clean(value).replace(/\bTelehealth\b/gi, 'Video');
}

function plannerEventPatientName(patientName: any, patient: any) {
  const sourceName = clean(patientName);
  if (sourceName) {
    return patientNameHasRepeatedMiddleToken(sourceName, patient?.middleName)
      ? patient?.displayName || patient?.fullName || sourceName
      : sourceName;
  }
  return patient?.displayName || patient?.fullName || 'Unknown patient';
}

function uniquePills(rawPills: any) {
  const seen = new Set();
  return rawPills
    .filter(Boolean)
    .map((pill: any) => ({
      ...pill,
      label: normalizePlannerLabel(pill.label),
    }))
    .filter((pill: any) => pill.label)
    .filter((pill: any) => {
      const key = pill.label.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function buildPlannerEventActions({
  canResume = false,
  canEnd = false,
  canRestore = false,
  canCancel = false,
  canReschedule = false,
  resumeSession = null,
  endEncounter = null,
  restoreSession = null,
  scheduledBot = null,
  phoneCall = null,
  telephoneEncounter = null,
}: Record<string, any> = {}) {
  return {
    canResume: Boolean(canResume),
    canEnd: Boolean(canEnd),
    canRestore: Boolean(canRestore),
    canCancel: Boolean(canCancel),
    canReschedule: Boolean(canReschedule),
    resumeSession: resumeSession || null,
    endEncounter: endEncounter || null,
    restoreSession: restoreSession || null,
    scheduledBot: scheduledBot || null,
    phoneCall: phoneCall || null,
    telephoneEncounter: telephoneEncounter || null,
  };
}

// The one PlannerEvent definition. Every planner card — schedule rows,
// unlinked rows, and patient-selection rows — must come through this factory
// so the card shape can never fork between producers.
export function buildPlannerEvent({
  id = '',
  stage = 'upcoming',
  source = 'unknown',
  sourceId = '',
  patient = null,
  patientName = '',
  time = '',
  sortDate = null,
  language = '',
  isTelehealth = false,
  durationLabel = '',
  recordedMinutes = 0,
  detailLine = '',
  summaryText = '',
  appointmentSummary = '',
  summaryVariants = [],
  summarySearchText = '',
  accentKey = '',
  statusLabel = '',
  metadataPills = [],
  modality = '',
  isDictation = false,
  actions = {},
}: Record<string, any> = {}): PlannerEvent {
  const minutes = Number(recordedMinutes);
  const cleanedSummaryVariants = Array.isArray(summaryVariants)
    ? summaryVariants.map(clean).filter(Boolean)
    : [];
  const resolvedAccent = clean(accentKey) || 'default';
  // Delivery modality, kept simple: a Zoom/telehealth session is Video, a
  // company-phone / telephonic session is Phone, everything else is In-person.
  // Producers can pass an explicit modality (e.g. a telephonic interpreter
  // encounter that isn't a `phoneCall` source) which wins over the inference.
  const resolvedModality =
    clean(modality) ||
    (isTelehealth
      ? 'video'
      : source === 'phoneCall' || resolvedAccent === 'phone'
        ? 'phone'
        : 'in_person');

  return {
    id: clean(id),
    stage,
    source,
    sourceId: clean(sourceId),
    patient,
    patientName: plannerEventPatientName(patientName, patient),
    time: clean(time),
    sortDate: sortDate || null,
    language: clean(language),
    isTelehealth: Boolean(isTelehealth),
    durationLabel: clean(durationLabel),
    recordedMinutes: Number.isFinite(minutes) && minutes > 0 ? minutes : 0,
    detailLine: normalizePlannerLabel(detailLine),
    summaryText: clean(summaryText),
    appointmentSummary: clean(appointmentSummary),
    // Every searchable note attached to this encounter (joint summary first,
    // then each member conversation's note) so encounter search reaches a
    // matching session note even when the headline summary doesn't contain it.
    summaryVariants: cleanedSummaryVariants,
    summarySearchText:
      clean(summarySearchText) || cleanedSummaryVariants.join('\n'),
    accentKey: resolvedAccent,
    modality: resolvedModality,
    isDictation: Boolean(isDictation),
    statusLabel: clean(statusLabel),
    metadataPills,
    actions: buildPlannerEventActions(actions),
  };
}

function buildPlannerEventMetadataPills(item: any, stage: any) {
  return uniquePills([
    item?.isTelehealth
      ? {key: 'video', iconKey: 'video', label: 'Video'}
      : null,
    stage !== 'active' && item?.durationLabel
      ? {key: 'duration', iconKey: 'duration', label: item.durationLabel}
      : null,
    item?.languageDialect
      ? {key: 'language', iconKey: 'language', label: item.languageDialect}
      : null,
  ]);
}

function removedConversationDisplayDate(conversation: any) {
  return (
    toJsDate(conversation?.end_time) ||
    toJsDate(conversation?.start_time) ||
    toJsDate(conversation?.removed_at)
  );
}

function phoneCallDisplayDate(call: any) {
  return (
    toJsDate(call?.end_time) ||
    toJsDate(call?.start_time) ||
    toJsDate(call?.created_at) ||
    toJsDate(call?.createdAt)
  );
}

function uniqueNonEmptyStrings(values: any[] = []) {
  const seen = new Set<string>();
  const result: string[] = [];
  values.forEach(value => {
    const text = clean(value);
    if (!text || seen.has(text)) return;
    seen.add(text);
    result.push(text);
  });
  return result;
}

// Every non-placeholder session note attached to an encounter, in member
// order. A multi-session encounter's headline `summary` is just one of these
// (often the joint or most-recent note), so search needs the whole set.
function encounterSummaryVariants(encounter: any, headlineSummary: string) {
  const conversations = Array.isArray(encounter?.conversations)
    ? encounter.conversations
    : [];
  const conversationSummaries = conversations.map((conversation: any) =>
    isPlaceholderEncounterSummary(conversation?.summary)
      ? ''
      : clean(conversation?.summary),
  );
  return uniqueNonEmptyStrings([headlineSummary, ...conversationSummaries]);
}

export function plannerEventFromEncounterView(
  encounter: any,
  viewerUserId: any = null,
) {
  if (!encounter) return null;
  const isActive = isEncounterRecording(encounter);
  const stage = isActive ? 'active' : 'completed';
  const section = isActive ? 'incomplete' : 'completed';
  const summary = isPlaceholderEncounterSummary(encounter?.summary)
    ? ''
    : clean(encounter?.summary);
  const summaryVariants = encounterSummaryVariants(encounter, summary);
  const modality = resolveEncounterModality(encounter);
  // A telephonic (SIP/phone) visit is a Phone visit even when the encounter's
  // workflow_type says telehealth. Web dial-out interpretation rooms are stamped
  // `telehealth_interpretation` but the patient is reached by phone, so the
  // phone signal must win over the telehealth one — otherwise the row shows
  // "Video" for what is really a phone call. Resolve phone first.
  const isPhone = isPhoneEncounter(encounter);
  const isTelehealth = !isPhone && isTelehealthEncounter(encounter);
  const durationMinutes = encounterRecordedMinutes(encounter);
  const durationLabel = durationMinutes > 0 ? `${durationMinutes} min` : '';
  const resumeSession = representativeEncounterSession(encounter, viewerUserId);

  return buildPlannerEvent({
    id: `${section}-encounter-${encounter?.id || 'unknown'}`,
    stage,
    source: encounter?.isSynthetic ? 'conversation' : 'encounter',
    sourceId: encounter?.id || null,
    patient: plannerPatientFromEncounterView(encounter),
    patientName: encounter?.patient_name,
    sortDate: encounterDisplayDate(encounter),
    language: encounterLanguage(encounter),
    isTelehealth,
    modality: isPhone ? 'phone' : '',
    durationLabel,
    recordedMinutes: isActive ? durationMinutes : 0,
    detailLine: encounterDetailLine(encounter, viewerUserId),
    summaryText: summary,
    appointmentSummary: summary,
    summaryVariants,
    accentKey: modality.isScribe ? 'scribe' : 'interpreter',
    isDictation: modality.isDictation,
    metadataPills: buildPlannerEventMetadataPills(
      {
        isTelehealth,
        durationLabel,
        languageDialect: encounterLanguage(encounter),
      },
      stage,
    ),
    actions: {
      canResume: isActive && Boolean(resumeSession),
      canEnd: isActive,
      resumeSession,
      endEncounter: encounter,
    },
  });
}

export function plannerRecentActivityFromEncounterView(
  encounter: any,
  viewerUserId: any = null,
): PlannerRecentActivity | null {
  if (!encounter) return null;
  const isActive = isEncounterRecording(encounter);
  const section = isActive ? 'incomplete' : 'completed';
  const patient = plannerPatientFromEncounterView(encounter);
  const modality = resolveEncounterModality(encounter);
  const durationMinutes = encounterRecordedMinutes(encounter);
  const durationLabel = durationMinutes > 0 ? `${durationMinutes} min` : '';
  const summary = isPlaceholderEncounterSummary(encounter?.summary)
    ? ''
    : clean(encounter?.summary);
  const occurredAt = encounterDisplayDate(encounter);
  const resumeSession = representativeEncounterSession(encounter, viewerUserId);

  return {
    id: `recent-activity-${encounter?.id || 'unknown'}`,
    stage: isActive ? 'active' : 'completed',
    source: encounter?.isSynthetic ? 'conversation' : 'encounter',
    sourceId: clean(encounter?.id),
    patient,
    patientName: clean(encounter?.patient_name) || 'Unknown patient',
    occurredAt,
    sortDate: occurredAt,
    // This is launch seed data only. Card metadata comes from `patient`.
    language: encounterLanguage(encounter),
    durationLabel,
    recordedMinutes: isActive ? durationMinutes : 0,
    detailLine: encounterDetailLine(encounter, viewerUserId),
    accentKey: modality.isScribe ? 'scribe' : 'interpreter',
    isDictation: modality.isDictation,
    // Phone wins over telehealth: a telephonic dial-out interpretation room is
    // tagged `telehealth_interpretation` but is delivered over the phone.
    modality: isPhoneEncounter(encounter)
      ? 'phone'
      : isTelehealthEncounter(encounter)
        ? 'video'
        : 'in_person',
    summary,
    summaryText: summary,
    metadataText: plannerPatientMetadataText(patient),
    actions: buildPlannerEventActions({
      canResume: isActive && Boolean(resumeSession),
      canEnd: isActive,
      resumeSession,
      endEncounter: encounter,
    }),
  };
}

export function plannerRecentActivityFromPlannerEvent(
  event: any,
): PlannerRecentActivity | null {
  if (!event) return null;
  const summary = clean(event.summaryText || event.appointmentSummary);

  return {
    id: event.id,
    stage: event.stage || 'completed',
    source: event.source || 'unknown',
    sourceId: clean(event.sourceId),
    patient: event.patient || null,
    patientName: clean(event.patientName) || 'Unknown patient',
    occurredAt: event.sortDate || null,
    sortDate: event.sortDate || null,
    language: clean(event.language),
    durationLabel: clean(event.durationLabel),
    recordedMinutes: Number(event.recordedMinutes) || 0,
    detailLine: clean(event.detailLine),
    accentKey: clean(event.accentKey),
    modality: clean(event.modality) || 'in_person',
    isDictation: Boolean(event.isDictation),
    summary,
    summaryText: summary,
    metadataText: plannerPatientMetadataText(event.patient),
    actions: buildPlannerEventActions(event.actions),
  };
}

export function plannerEventFromRemovedConversation(conversation: any) {
  if (!conversation) return null;
  const language = removedConversationLanguage(conversation);
  const durationMinutes = conversationRecordedMinutes(conversation);
  const durationLabel =
    durationMinutes > 0 ? `${Math.max(1, Math.ceil(durationMinutes))} min` : '';
  const summary = isPlaceholderEncounterSummary(conversation?.summary)
    ? ''
    : clean(conversation?.summary);
  const modality = resolveEncounterModality(conversation);
  // Phone wins over telehealth (see plannerEventFromEncounterView): a
  // telephonic visit stays a Phone visit even with a telehealth workflow_type.
  const isPhone = isPhoneEncounter(conversation);
  const isTelehealth = !isPhone && isTelehealthEncounter(conversation);

  return buildPlannerEvent({
    id: `removed-conversation-${conversation?.id || 'unknown'}`,
    stage: 'completed',
    source: 'conversation',
    sourceId: conversation?.id || null,
    patient: plannerPatientFromRemovedConversation(conversation),
    patientName: conversation?.patient_name,
    sortDate: removedConversationDisplayDate(conversation),
    language,
    isTelehealth,
    modality: isPhone ? 'phone' : '',
    durationLabel,
    detailLine: titleCase(
      conversation?.specialty || conversation?.specialty_name,
    ),
    summaryText: summary,
    appointmentSummary: summary,
    accentKey: modality.isScribe ? 'scribe' : 'interpreter',
    isDictation: modality.isDictation,
    metadataPills: buildPlannerEventMetadataPills(
      {
        isTelehealth,
        durationLabel,
        languageDialect: language,
      },
      'completed',
    ),
    statusLabel: 'Removed',
    actions: {
      canRestore: true,
      restoreSession: conversation,
    },
  });
}

const PHONE_SUMMARY_PLACEHOLDERS = new Set(['Telephonic Interpretation']);

export function plannerEventFromPhoneCall(call: any) {
  if (!call) return null;
  const language = phoneCallLanguage(call);
  const durationMinutes =
    recordedMinutesFromValue(call?.minutes) ||
    recordedMinutesFromSeconds(call?.elapsed_seconds, call?.elapsedSeconds);
  const durationLabel =
    durationMinutes > 0
      ? `${Math.max(1, Math.round(durationMinutes))} min`
      : '';
  const rawSummary = clean(call?.summary);
  const summary = PHONE_SUMMARY_PLACEHOLDERS.has(rawSummary) ? '' : rawSummary;
  const accessCode = clean(call?.access_code || call?.accessCode);
  const phoneName =
    summary ||
    (accessCode ? `Access code ${accessCode}` : 'Company phone call');

  return buildPlannerEvent({
    id: `phone-call-${call?.id || 'unknown'}`,
    stage: 'completed',
    source: 'phoneCall',
    sourceId: call?.id || null,
    patient: null,
    patientName: phoneName,
    sortDate: phoneCallDisplayDate(call),
    language,
    durationLabel,
    detailLine: accessCode
      ? `Company line · access code ${accessCode}`
      : 'Company phone line',
    summaryText: summary,
    appointmentSummary: summary,
    accentKey: 'phone',
    statusLabel: accessCode ? `Phone · access ${accessCode}` : 'Phone',
    metadataPills: buildPlannerEventMetadataPills(
      {
        durationLabel,
        languageDialect: language,
      },
      'completed',
    ),
    actions: {
      phoneCall: call,
    },
  });
}

const TELEPHONE_ENCOUNTER_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  open: 'Open',
  closed: 'Closed',
};

// Map a telephone-encounter record onto the shared planner event shape so it
// can be selected and rendered in the detail pane just like a phone call or a
// regular encounter. The raw record rides along on `actions.telephoneEncounter`
// for the detail view to edit.
export function plannerEventFromTelephoneEncounter(te: any) {
  if (!te) return null;
  const language = clean(te?.translator?.language);
  const dialect = clean(te?.translator?.dialect);
  const languageLabel = language
    ? dialect
      ? `${language} (${dialect})`
      : language
    : '';
  const reason = clean(te?.reasonText);
  const message = clean(te?.message);
  const summary = message || reason;
  const patientName = clean(te?.patientName) || 'Unknown caller';
  const statusLabel =
    TELEPHONE_ENCOUNTER_STATUS_LABELS[te?.status] || 'Telephone Encounter';
  const patient = plannerPatientFromRemovedConversation({
    patient_id: te?.patientId,
    ehr_patient_id: te?.ehrPatientId,
    patient_name: patientName,
  });
  const patientWithLanguage = patient
    ? {...patient, language: patient.language || languageLabel}
    : null;

  return buildPlannerEvent({
    id: `telephone-encounter-${te?.id || 'unknown'}`,
    stage: 'completed',
    source: 'telephoneEncounter',
    sourceId: te?.id || null,
    patient: patientWithLanguage,
    patientName,
    sortDate: toJsDate(te?.updatedAt) || toJsDate(te?.createdAt) || new Date(),
    language: languageLabel,
    detailLine: reason || 'Telephone Encounter',
    summaryText: summary,
    appointmentSummary: summary,
    summaryVariants: [summary, reason].filter(Boolean),
    accentKey: 'phone',
    statusLabel,
    metadataPills: buildPlannerEventMetadataPills(
      {languageDialect: languageLabel},
      'completed',
    ),
    actions: {
      telephoneEncounter: te,
    },
  });
}

// Translate a selected planner event into the language seed consumed by the
// patient panel's new encounter start box.
export function buildEncounterComposerSeedFromEvent(event: any) {
  if (!event) return null;
  const language = clean(event.language || event.patient?.language);

  return {
    key: event.id,
    language,
    dialect: clean(event.patient?.dialect),
  };
}

export function plannerEventFromScheduleItem(
  item: any,
  sectionKey = 'upcoming',
) {
  if (!item) return null;
  const stage = stageForSection(sectionKey);
  const source = sourceForItem(item);
  const patient = plannerPatientFromScheduleItem(item);
  const patientName = plannerEventPatientName(item.patientName, patient);

  return buildPlannerEvent({
    id:
      clean(item.id) ||
      `${sectionKey}-${source}-${item.sourceId || patientName}`,
    stage,
    source,
    sourceId: item.sourceId,
    patient,
    patientName,
    time: item.time,
    sortDate: item.sortDate,
    language: item.languageDialect,
    isTelehealth: item.isTelehealth,
    durationLabel: item.durationLabel,
    recordedMinutes: item.elapsedMinutes,
    detailLine: item.detailLine,
    summaryText: item.conversationSummary || item.summaryText,
    appointmentSummary:
      item?.appointment?.reason || item.conversationSummary || item.summaryText,
    accentKey: item.accentKey,
    statusLabel: item.statusLabel,
    metadataPills: buildPlannerEventMetadataPills(item, stage),
    actions: {
      canResume: stage === 'active' && Boolean(item.session),
      canEnd: stage === 'active' && Boolean(item.encounter),
      canCancel: source === 'scheduledBot',
      canReschedule: source === 'scheduledBot',
      resumeSession: item.session,
      endEncounter: item.encounter,
      scheduledBot: item.scheduledBot || null,
      phoneCall: item.phoneCall || null,
    },
  });
}

export function getVisiblePlannerEventSections(schedule: any) {
  return getVisiblePlannerScheduleSections(schedule)
    .map(section => ({
      key: section.key,
      label: section.label,
      defaultOpen: section.defaultOpen,
      events: section.items
        .map((item: any) => plannerEventFromScheduleItem(item, section.key))
        .filter(Boolean),
    }))
    .filter(section => section.events.length > 0);
}

export function isPlannerScheduleEmpty(schedule: any) {
  return (
    getPlannerScheduleTotal(getVisiblePlannerScheduleSections(schedule)) === 0
  );
}
